import { fetchMessageMediumsQuery } from 'api/queries';
import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import {
  selectIsContextualizedMetricsFeatureFlagOn,
  selectIsEmbeddedMessagingCoreFeatureFlagOn,
  selectIsMetricDefinitionsFeatureFlagOn,
} from 'core/userContext';
import { AllMessageMedium } from 'modules/domains/campaign';
import { MetricDefinitionDrawer } from 'modules/metricDefinitions';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { metricSentenceCase, sortBySelectionOrder } from 'modules/productAnalytics/utils';
import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { MultiFilterSelect, MultiSelect } from 'ui/selects';
import { body2Bold, colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { useGraphql } from 'utils/hooks/useGraphql';
import { CampaignCategoryArr, CampaignMetricLabels, MediumOptions } from '../constants';
import { useCampaignAndMetricFiltersContext } from '../context';
import { GqlFetchMessageMediumsResponse, MetricOption } from '../types';
import {
  createMetricOptions,
  filterMetricsByMedium,
  filterSelectedMetricsByMedium,
  getNiceMediumList,
} from '../utils';
import { FilterBuilder } from './FilterBuilder';
import { MetricsLoadingSkeleton } from './MetricsLoadingSkeleton';

type WrapperProps = {
  mainContentSize: number;
  showApplyButtons: boolean;
};

type StyledMultiFilterSelectWrapperProps = {
  mainContentSize: number;
};

const StyledWrapper = styled.div<WrapperProps>`
  min-width: ${pxToRem(392)};
  max-width: ${pxToRem(392)};
  padding: 0 ${spacing.double};
  background: ${colors.backgroundPrimary};
  position: relative;
  border-right: ${spacing.single} solid ${colors.borderPrimary};
  ${({ showApplyButtons }) =>
    showApplyButtons
      ? `
      &&{
        padding: 0 ${spacing.double};
      }
  & > div:not(:nth-last-child(-n + 2)) {
    border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  }`
      : `  & > div {
    border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  }
  & > div:last-child {
    border-bottom: none;
  }`}
`;

const StyledTitle = styled.div`
  ${body2Bold}
  display: flex;
  align-items: center;
  margin-bottom: ${spacing.threeQuarter};
`;

const StyledSection = styled.div`
  padding: ${spacing.double} 0;

  &:last-child {
    padding: ${spacing.double} 0 0;
    margin-bottom: ${spacing.double};
  }
`;

const StyledMultiFilterSelectWrapper = styled.div<StyledMultiFilterSelectWrapperProps>`
  max-height: ${({ mainContentSize }) => pxToRem(mainContentSize - 300)};
  overflow-y: auto;
  z-index: 1;

  > div {
    width: 100%;
    z-index: 1;
    > div {
      width: 100%;
      max-height: 100%;
      padding: 0;
      overflow-x: hidden;
    }
  }
`;

// For every category, grab the metrics that have that categoryid and sort them alphabetically
const sortMetricsByCat = (labels: MetricOption[]) => {
  return CampaignCategoryArr.map((cat) =>
    labels.filter((label) => label.categoryId === cat).sort((a, b) => (a.label < b.label ? -1 : 1)),
  ).flat();
};

type Props = {
  mainContentSize: number;
  showApplyButtons: boolean;
};

export const FilterSidePanel: FC<React.PropsWithChildren<Props>> = ({
  mainContentSize,
  showApplyButtons,
  children,
}) => {
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();
  const metricDefFFOn = useSelector(selectIsMetricDefinitionsFeatureFlagOn);
  const contextualizedMetricsFFOn = useSelector(selectIsContextualizedMetricsFeatureFlagOn);
  const { selectedCrossMetrics, setSelectedCrossMetrics, selectedMediums, setSelectedMediums } =
    useCampaignAndMetricFiltersContext();

  const { data: messageMediumData = {} }: GqlFetchMessageMediumsResponse =
    useGraphql(fetchMessageMediumsQuery);

  const { highLevelMetrics, analyticsMetrics, metricsLoading } = useMetricsContext();
  const excludedMediums: AllMessageMedium[] = isWhatsAppFFOn ? [] : [WhatsApp];

  useEffect(() => {
    const filteredSelectedMetrics = filterSelectedMetricsByMedium(
      selectedMediums,
      selectedCrossMetrics,
      highLevelMetrics,
      analyticsMetrics,
    );
    setSelectedCrossMetrics(filteredSelectedMetrics);
  }, [selectedMediums]);

  const messageMediums =
    messageMediumData?.messageMediums
      ?.concat(isEmbeddedMessagingEnabled ? [EmbeddedMessage] : [])
      ?.filter((med: string) => isWhatsAppFFOn || med !== WhatsApp)
      ?.map((med) => ({
        id: med,
        label: MediumOptions[med],
        value: med,
      })) || [];

  const filteredHighLevelMetrics = filterMetricsByMedium(selectedMediums, highLevelMetrics);
  const filteredHighLevelMetricsOptions = createMetricOptions(
    selectedMediums,
    filteredHighLevelMetrics,
    excludedMediums,
  );
  const filteredAnalyticsMetrics = filterMetricsByMedium(selectedMediums, analyticsMetrics);
  const filteredAnalyticsMetricsOptions = createMetricOptions(
    selectedMediums,
    filteredAnalyticsMetrics,
    excludedMediums,
  );
  const options = [...filteredHighLevelMetricsOptions, ...filteredAnalyticsMetricsOptions];

  return (
    <StyledWrapper mainContentSize={mainContentSize} showApplyButtons={showApplyButtons}>
      <StyledSection>
        <>
          <StyledTitle>Campaign filters</StyledTitle>
          <MultiSelect
            defaultValues={selectedMediums}
            options={messageMediums}
            isFullWidth
            placeholder="Select a campaign medium"
            onSelect={(val) => {
              setSelectedMediums(sortBySelectionOrder(selectedMediums, val));
            }}
            selectValueDisplayText={getNiceMediumList(selectedMediums)}
          />
          <FilterBuilder />
        </>
      </StyledSection>
      <StyledSection>
        <StyledTitle>
          Metrics
          {metricDefFFOn && !contextualizedMetricsFFOn && <MetricDefinitionDrawer />}
        </StyledTitle>
        {metricsLoading ? (
          <MetricsLoadingSkeleton />
        ) : (
          <StyledMultiFilterSelectWrapper mainContentSize={mainContentSize}>
            <MultiFilterSelect
              content={{
                clearText: 'Clear',
                selectNone: 'Select none',
                selectAll: 'Select all',
              }}
              placeholder={'0 types of metrics'}
              defaultValues={selectedCrossMetrics}
              inputContent={{ filterPlaceholder: 'Search all metrics' }}
              displayContent={{
                multipleSelectedText: 'types of metrics',
              }}
              onSelect={(val) => {
                const sortedVal = sortBySelectionOrder(selectedCrossMetrics, val);
                setSelectedCrossMetrics(sortedVal);
              }}
              id={`campaign-metrics`}
              title={`campaign-metrics`}
              options={sortMetricsByCat(options).map((opt) => ({
                ...opt,
                label: metricSentenceCase(opt.label),
              }))}
              categoryLabels={CampaignMetricLabels}
              isEmbedded
            />
          </StyledMultiFilterSelectWrapper>
        )}
      </StyledSection>
      {children}
    </StyledWrapper>
  );
};
